'use strict';

import { DataModelTypes } from '../../model/data-model/enumerations';

ODataQueryExecutorFactory.$inject = [
	'mx.shell.SearchProviderService',
	'mx.SolutionBuilderAgent.ExternalServiceManager',
	'mx.SolutionBuilderAgent.EntityService',
	'mx.SolutionBuilderAgent.SearchQueryService',
	'mx.shell.DownloadHelpService',
	'mx.shell.NotificationService',
	'$timeout',
	'mx.SolutionBuilderAgent.Http',
	'mx.sb.Agent.DataQuery.Enums',
	'mx.SolutionBuilderAgent.ServiceRequestParamsPreprocessor',
	'mx.SolutionBuilderAgent.SearchQueryGroup',
	'mx.sb.Agent.DataQuery.FilterOperatorsFactory',
	'mx.shell.UserProfileService',
];
export default function ODataQueryExecutorFactory(
	searchService,
	externalServiceManager,
	entityService,
	searchQueryService,
	downloadHelpService,
	notificationService,
	$timeout,
	http,
	dataQueryEnums,
	ServiceRequestParamsPreprocessor,
	SearchQueryGroup,
	filterOperatorsFactory,
	userProfileService
) {
	function ODataQueryExecutor() {
		let dataQuery;
		let dataModel;
		let widgetId;
		let structureNodeId;
		let executeOptions = {};
		let urlCfg = {};
		let userId;
		let filterExpression;
		const ColumnSortingEnum = dataQueryEnums.ColumnSortingEnum;

		return {
			run: run,
			sortingBuild: sortingBuild,
			paginationBuild: paginationBuild,
			searchBuild: searchBuild,
			getODataSearchFilter: getODataSearchFilter,
			init: init,
			getTypes: getTypes,
			supportsGrouping: () => false,
			columnsBuild: () => '',
			getServiceUrl: getServiceUrl,
			quickFiltersBuild: quickFiltersBuild,
			setUrlCfg: (_urlCfg) => (urlCfg = _urlCfg),
			usePostBack: true,
			executeExportData: executeExportData,
			getIdentifier: () => executeOptions.typeInfo.Identifier,
		};

		function getServiceUrl() {
			return executeOptions.listUrl;
		}

		function getTypes() {
			const type = executeOptions.typeInfo.TypeName;
			return type ? [type] : [];
		}

		function init(type, params) {
			dataModel = params.dataModel || {};
			filterExpression = params.filterExpression;
			dataQuery = params.dataQuery;
			widgetId = params.id;
			structureNodeId = params.structureNodeId;
			executeOptions = externalServiceManager.getListExecuteOptions(dataQuery, mx.SolutionBuilderAgent.SupportedOperations.ReadObjectsByODataQuery, entityService);
			return [ServiceRequestParamsPreprocessor];
		}

		async function searchBuild() {
			const currentSearch = searchService.currentSearch();
			userId = (await userProfileService.get()).id;
			const { filters, keyword, searchForm, filtersOperator } = currentSearch;
			return getODataSearchFilter(dataQuery, keyword, filters, searchForm, filtersOperator);
		}

		async function getODataSearchFilter(dataQuery, keyword, filterIds, searchForm, filtersOperator) {
			const allWidgetFilters = await searchQueryService.getFilters(dataQuery.Id, widgetId);
			const filterGroups = allWidgetFilters
				.filter((widgetFilter) => filterIds.includes(widgetFilter.Id))
				.map((filter) => new SearchQueryGroup(JSON.parse(filter.QueryFilter)));

			const keywordExpression = executeOptions.searchByKeywordFuncs.processKeyword(keyword);

			const search = new SearchQueryGroup();
			search.groups = filterGroups;
			if (searchForm) {
				search.groups.push(searchForm);
			}
			const query = { group: search };
			await searchQueryService.initQueryByDataQuery(query, dataQuery);
			search.attachedOperator = searchService.getQueryOperatorText(filtersOperator, 'and');

			search.logicalOperator =
				search.attachedOperator === 'and' ? mx.SolutionBuilderAgent.SearchQueryLogicalOperator.AND : mx.SolutionBuilderAgent.SearchQueryLogicalOperator.OR;

			const expression = processSearchGroup(search);

			const filters = [expression, filterExpression, keywordExpression].filter((item) => item);

			if (filters.length > 0) {
				return `$filter=(${filters.join(') and (')})`;
			}
		}

		function quickFiltersBuild() {}

		function processSearchGroup(searchGroup) {
			if (!searchGroup) {
				return;
			}
			const conditions = [];

			searchGroup.conditions.forEach((condition) => {
				let result;
				if (!dataModel[dataQuery.Name] || !dataModel[dataQuery.Name].hasOwnProperty(condition.property)) {
					result = processSearchCondition(condition);
				}

				if (result) {
					conditions.push(result);
				}
			});

			searchGroup.groups.forEach((group) => {
				const groupAsCondition = processSearchGroup(group);

				if (groupAsCondition) {
					conditions.push(groupAsCondition);
				}
			});

			return conditions.join(searchGroup.logicalOperator === 1 ? ' and ' : ' or ');
		}

		function processSearchCondition(condition) {
			if (condition.value.length === 0 && (condition.type !== 'StringType' || (condition.operator !== 14 && condition.operator !== 15))) {
				return;
			}
			const conditions = [];
			let operator;
			let not = '';
			let linkOperator;
			const property = condition.__property;
			const expression = _getExpression(property.SearchExpression || property.Expression || property.Name, 'search');

			switch (condition.operator) {
				case 14:
					return `((${expression} eq null) or (${expression} eq ''))`;
				case 15:
					return `((${expression} ne null) and (${expression} ne ''))`;
			}

			if (condition.__enumeration) {
				if (condition.__flags) {
					operator = ' has ';
					linkOperator = ' or ';
					if (condition.operator !== 10) {
						not = ' not ';
					}
				} else {
					operator = condition.operator === 10 ? ' eq ' : ' ne ';
					linkOperator = condition.operator === 10 ? ' or ' : ' and ';
				}

				condition.value.forEach((val) => {
					const value = condition.__namespace ? `${condition.__namespace}'${val}'` : val;
					conditions.push(expression + operator + value);
				});

				const groupNeed = condition.__flags ? true : conditions.length > 1;
				return not + (groupNeed ? `(${conditions.join(linkOperator)})` : conditions[0]);
			}

			linkOperator = condition.operator === 10 ? ' or ' : ' and ';
			condition.value.forEach(function (val, index) {
				if (val || condition.type === DataModelTypes.BoolType.name) {
					const conditionExpression = getConditionExpression(expression, condition.operator, condition.type, val, index);
					if (conditionExpression) {
						conditions.push(conditionExpression);
					}
				}
			});

			return conditions.length > 1 ? '(' + conditions.join(linkOperator) + ')' : conditions[0];
		}

		function getConditionExpression(expression, operator, type, value, index) {
			const originalValue = value;

			if (value === '@me' && userId) {
				value = userId;
			} else if (value === '@today') {
				value = new Date();
			}

			if (type === DataModelTypes.StringType.name) {
				value = `'${value.toLowerCase()}'`;
			} else if (type === DataModelTypes.DateType.name && value instanceof Date) {
				value = value.toISOString();
			}

			const operators = filterOperatorsFactory.operators;

			const getContainsAnyWordFilter = (value, expression) => {
				return filterOperatorsFactory.getWords(value).reduce((accumulator, word) => {
					const wordOperator = accumulator.length ? ' or ' : '';
					return `${accumulator}${wordOperator} contains(tolower(${expression}), '${word}')`;
				}, '');
			};

			const getContainsAllWordFilter = (value, expression) => {
				return filterOperatorsFactory.getWords(value).reduce((accumulator, word) => {
					const wordOperator = accumulator.length ? ' and ' : '';
					return `${accumulator}${wordOperator} contains(tolower(${expression}), '${word}')`;
				}, '');
			};

			switch (operator) {
				case operators.Equals.value:
					return type === DataModelTypes.StringType.name ? `tolower(${expression}) eq ${value}` : `${expression} eq ${value}`;
				case operators.NotEquals.value:
					return type === DataModelTypes.StringType.name ? `tolower(${expression}) ne ${value}` : `${expression} ne ${value}`;
				case operators.LowerThan.value:
					return `${expression} lt ${value}`;
				case operators.LowerThanOrEquals.value:
					return `${expression} le ${value}`;
				case operators.GreaterThan.value:
					return `${expression} gt ${value}`;
				case operators.GreaterThanOrEquals:
					return `${expression} ge ${value}`;
				case operators.Contains.value:
					return `contains(tolower(${expression}), ${value})`;
				case operators.StartsWith.value:
					return `startswith(tolower(${expression}),${value})`;
				case operators.EndsWith.value:
					return `endswith(tolower(${expression}),${value})`;
				case operators.In.value:
					return `${expression} eq ${value}`;
				case operators.NotIn.value:
					return `${expression} ne ${value}`;
				case operators.Between.value:
					if (type === DataModelTypes.DateType.name) {
						const dateOperator = index === 0 ? ' ge ' : ' le ';
						return expression + dateOperator + value;
					}
					return;
				case operators.FlexDateBetween.value:
					return;
				case operators.NotContains.value:
					return `not contains(tolower(${expression}), ${value})`;
				case operators.ContainsAnyWord.value:
					return getContainsAnyWordFilter(originalValue, expression);
				case operators.ContainsAllWords.value:
					return getContainsAllWordFilter(originalValue, expression);
				case operators.DoesNotContainAnyWord.value: {
					const containsAnyWordFilter = getContainsAnyWordFilter(originalValue, expression);
					return `not (${containsAnyWordFilter})`;
				}
				case operators.DoesNotContainAllWords.value: {
					const containsAllWordFilter = getContainsAllWordFilter(originalValue, expression);
					return `not (${containsAllWordFilter})`;
				}
				default:
					return;
			}
		}

		function sortingBuild(sortColumns) {
			if (!Array.isArray(sortColumns) || !sortColumns.length) {
				return '';
			}
			return sortColumns.reduce((accumulator, column, index) => {
				const direction = column.Sorting === ColumnSortingEnum.Ascending ? 'asc' : 'desc';
				return accumulator + (index > 0 ? ',' : '') + _getExpression(column.SortingExpression || column.Expression || column.Name, 'sort') + ' ' + direction;
			}, '$orderby=');
		}

		function paginationBuild(pageSize, page) {
			return pageSize > 0 ? '$top=' + pageSize + (page > 0 ? '&$skip=' + page * pageSize : '') : '';
		}

		function prepareUrl(baseUrl, parts) {
			let queryString = '';

			parts.forEach((part) => {
				queryString = (queryString ? `${queryString}&` : '') + part;
			});

			baseUrl += (baseUrl.indexOf('?') === -1 ? '?' : '&') + queryString;
			return baseUrl;
		}

		function run(parts, progressHandler) {
			const dataRequestUrl = prepareUrl(urlCfg.path, executeOptions.countOptions.executeExternalService ? parts : parts.concat('$count=true'));
			const options = {};
			if (typeof progressHandler === 'function') {
				options.eventHandlers = {
					progress: progressHandler,
				};
			}

			return executeOptions.httpProvider
				.get(dataRequestUrl, undefined, options)
				.then(executeOptions.processResponse)
				.then(function (response) {
					let data = executeOptions.getProcessedDataFromObject(response);

					if (!Array.isArray(data)) {
						data = [];
						mx.console.error('Wrong response format for: ', dataRequestUrl);
					}

					if (executeOptions.countOptions.executeExternalService) {
						const dataRequestUrlCount = prepareUrl(executeOptions.countOptions.dataRequestUrlCount, parts);
						return executeOptions.httpProvider
							.get(dataRequestUrlCount)
							.then(executeOptions.processResponse, () => -1)
							.then((result) => ({
								data: data,
								totalCount: Number(result),
							}));
					}
					return {
						data: data,
						totalCount: Number(executeOptions.countOptions.getFromObject(response) || 0),
					};
				});
		}

		function _getExpression(expression, type) {
			const allExpressions = JSON.parse(expression.match(/{(.+?)}/g));
			if (allExpressions && allExpressions[type]) {
				return allExpressions[type].replace(/\./g, '/');
			}

			return expression
				.replace(/\/\*.+?\*\/|\/\/.*(?=[\n\r])/g, '')
				.trim()
				.replace(/\./g, '/');
		}

		function executeExportData() {
			const headers = { responseType: 'arraybuffer' };

			return searchBuild().then((search) =>
				http.get(getExportDataUrl(search), '', headers, false, true).then((response) => {
					downloadHelpService.downloadContent(response, response.data);
				})
			);
		}

		function getExportDataUrl(search) {
			const baseUrl = getBaseUrl();
			let exportMarker = '?export=true';
			const structureParam = structureBuild(structureNodeId);
			if (structureParam) {
				exportMarker += `&${structureParam}`;
			}
			if (search) {
				exportMarker += `&${search}`;
			}
			return baseUrl + exportMarker;
		}

		function getBaseUrl() {
			const serviceUrl = getServiceUrl();
			const exportIndex = serviceUrl.indexOf('?export');
			if (exportIndex === -1) {
				throw new Error('Export not allowed for this data query');
			}
			return serviceUrl.substr(0, exportIndex);
		}

		function structureBuild(structureId) {
			if (structureId) {
				return `structureNodeId=${structureId}`;
			}
		}
	}

	return ODataQueryExecutor;
}
